import React, { createContext, useContext, useEffect, useState, ReactNode, FC } from 'react';

interface ColorModeContextValue {
  mode: 'light' | 'dark';
  setMode: (mode: 'light' | 'dark') => void;
}

const ColorModeContext = createContext<ColorModeContextValue>({
  mode: 'light',
  setMode: () => {},
});

export const ColorModeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('theme') : null;
      if (stored === 'dark' || stored === 'light') return stored;
    } catch {}
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    // Apply class and persist choice
    document.documentElement.classList.toggle('dark', mode === 'dark');
    try { localStorage.setItem('theme', mode); } catch {}
  }, [mode]);

  useEffect(() => {
    // Sync across tabs/windows
    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'theme') return;
      const value = e.newValue;
      if (value === 'dark' || value === 'light') {
        setMode(value);
      } else {
        const sys = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        setMode(sys);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <ColorModeContext.Provider value={{ mode, setMode }}>
      {children}
    </ColorModeContext.Provider>
  );
};

export const useColorMode = () => useContext(ColorModeContext); 